import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { NoteDraft } from '@/components/investigations/InvestigationNoteComposer'
import { useAuth } from '@/contexts/AuthContext'
import { analyticHypothesisKeys } from '@/features/analytic-hypotheses/queries'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import type { Investigation, InvestigationNote } from '@/types/investigation'

/**
 * Adds a note of a kind to an investigation (PRD-0075 R-E4). The server logs an assessment
 * note's `assessment_changed`; the hypothesis it shows on is read again.
 */
export function useInvestigationNoteComposer(inv: Investigation) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const storeApi = useInvestigationsStoreApi()
  const queryClient = useQueryClient()

  const onAdd = async ({ text, kind }: NoteDraft): Promise<string | null> => {
    const note: InvestigationNote = {
      id: crypto.randomUUID(),
      text,
      kind,
      authorId: user?.id,
      authorName:
        user?.full_name || user?.username || t('investigations.page.note.composer.someone'),
      createdAt: new Date().toISOString(),
    }
    try {
      await storeApi.getState().updateById(inv.id, { notes: [...inv.notes, note] })
    } catch {
      return t('investigations.page.note.composer.failed')
    }
    if (kind === 'assessment' && inv.hypothesisRef) {
      void queryClient.invalidateQueries({ queryKey: analyticHypothesisKeys.all(inv.caseId) })
    }
    return null
  }

  return { onAdd, disabled: inv.readOnly }
}
